import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Activity, Store, Boxes, LineChart, Layers } from "lucide-react";
import { AngadyaLogo } from "../components/Logo";
import { useSession } from "../context/SessionContext";

// Reusable in-hero mini stat
const HeroStat = ({ label, value, muted }) => (
  <div>
    <div
      className="text-[10px] font-bold uppercase tracking-widest"
      style={{ color: "rgba(255,255,255,0.55)" }}
    >
      {label}
    </div>
    <div
      className="mt-1 tabular-nums font-extrabold text-lg"
      style={{ letterSpacing: "-0.02em", color: muted ? "rgba(255,255,255,0.80)" : "#FFFFFF" }}
    >
      {value}
    </div>
  </div>
);

// Role-specific accent (icon + gradient) so cards read distinct at a glance
const ROLE_ACCENT = {
  owner:            { Icon: Layers,      grad: "linear-gradient(135deg, #00B4D8 0%, #7C6FF0 100%)", tag: "Full access" },
  regional_manager: { Icon: LineChart,   grad: "linear-gradient(135deg, #7C6FF0 0%, #B57DF6 100%)" },
  procurement_admin:{ Icon: Boxes,       grad: "linear-gradient(135deg, #12B76A 0%, #00B4D8 100%)" },
  hr_admin:         { Icon: Activity,    grad: "linear-gradient(135deg, #F5A623 0%, #F04438 100%)" },
  store_manager:    { Icon: Store,       grad: "linear-gradient(135deg, #14142B 0%, #4B4E80 100%)" },
};

const Landing = () => {
  const { bootstrap, loading, login } = useSession();
  const navigate = useNavigate();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  if (loading || !bootstrap) {
    return (
      <div className="min-h-screen flex items-center justify-center ang-paper" data-testid="landing-loading">
        <div className="font-bold text-ang-muted">Loading Angadya…</div>
      </div>
    );
  }

  const handleLogin = (role) => {
    if (role.id === "store_manager") {
      const branch = selectedBranch || bootstrap.branches[0].id;
      login({ role: role.id, branch_id: branch });
    } else if (role.id === "regional_manager") {
      const region = selectedRegion || bootstrap.regions[0];
      login({ role: role.id, region });
    } else {
      login({ role: role.id });
    }
    const modules = bootstrap.role_modules[role.id];
    const first = modules[0];
    navigate(`/app/${first}`);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="landing-page">
      {/* -------- LEFT HERO PANEL -------- */}
      <div
        className="ang-hero-bg text-white relative overflow-hidden flex flex-col justify-between px-8 sm:px-12 py-10 lg:py-14 lg:w-[42%] xl:w-[38%] lg:min-h-screen"
      >
        {/* Decorative blobs */}
        <div
          className="ang-blob absolute pointer-events-none"
          style={{
            top: -80, right: -60, width: 340, height: 340,
            background: "radial-gradient(circle, rgba(0,180,216,0.55) 0%, rgba(0,180,216,0) 70%)",
            filter: "blur(32px)",
          }}
        />
        <div
          className="ang-blob absolute pointer-events-none"
          style={{
            bottom: -110, left: -50, width: 380, height: 380,
            background: "radial-gradient(circle, rgba(124,111,240,0.60) 0%, rgba(124,111,240,0) 70%)",
            filter: "blur(36px)",
            animationDelay: "3s",
          }}
        />

        {/* TOP — Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <AngadyaLogo size={72} tone="light" />
          <div className="min-w-0">
            <div
              className="text-3xl font-extrabold leading-none"
              style={{ letterSpacing: "-0.028em" }}
            >
              Angadya
            </div>
            <div
              className="text-[11px] font-bold uppercase tracking-[0.22em] mt-2"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              Retail Intelligence
            </div>
          </div>
        </div>

        {/* MIDDLE — Tagline */}
        <div className="relative z-10 mt-16 lg:mt-0 max-w-md">
          <div
            className="inline-flex items-center gap-1.5 rounded-full ang-live-dot px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest"
            style={{ background: "rgba(18,183,106,0.16)", color: "#88F5B1" }}
          >
            Live · read-only demo
          </div>
          <h1
            className="mt-5 font-black leading-[0.98]"
            style={{ fontSize: "clamp(38px, 4.4vw, 56px)", letterSpacing: "-0.035em" }}
          >
            One dashboard.
            <br />
            <span className="ang-gradient-text">Every branch.</span>
          </h1>
          <p
            className="mt-5 text-[15px] leading-relaxed max-w-sm"
            style={{ color: "rgba(255,255,255,0.72)" }}
          >
            Angadya sits on top of your POS and ERP and turns 24 months of daily activity into
            operational answers. Pick a role — the surface reshapes to what you&apos;re accountable for.
          </p>
        </div>

        {/* BOTTOM — Hero stats + trust ribbon */}
        <div className="relative z-10 mt-16 lg:mt-0 space-y-6">
          <div className="grid grid-cols-3 gap-5">
            <HeroStat label="Branches" value="27" />
            <HeroStat label="SKUs modelled" value="102" />
            <HeroStat label="Data horizon" value="24 mo" muted />
          </div>
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1.5"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <ShieldCheck size={13} strokeWidth={2.4} style={{ color: "#88F5B1" }} />
            <span
              className="text-[10px] font-bold uppercase tracking-widest"
              style={{ color: "rgba(255,255,255,0.72)" }}
            >
              No credentials · nothing to install
            </span>
          </div>
        </div>
      </div>

      {/* -------- RIGHT PANEL — Role selection -------- */}
      <div className="flex-1 ang-paper px-6 sm:px-10 lg:px-14 py-10 lg:py-14 flex flex-col">
        <div className="max-w-2xl w-full mx-auto flex-1 flex flex-col">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-ang-muted">
              Step 1 · Choose your vantage point
            </div>
            <h2
              className="mt-2 text-3xl sm:text-4xl font-extrabold"
              style={{ letterSpacing: "-0.028em" }}
            >
              Sign in as…
            </h2>
            <p className="mt-2 text-sm text-ang-muted">
              Every role opens the same source of truth, scoped to what that person can see.
            </p>
          </div>

          <div className="mt-8 flex-1 space-y-3" data-testid="role-list">
            {bootstrap.roles.map((role, i) => (
              <RoleCard
                key={role.id}
                role={role}
                index={i}
                bootstrap={bootstrap}
                selectedBranch={selectedBranch}
                setSelectedBranch={setSelectedBranch}
                selectedRegion={selectedRegion}
                setSelectedRegion={setSelectedRegion}
                onLogin={handleLogin}
              />
            ))}
          </div>

          <div
            className="mt-8 pt-6 text-[11px] font-medium uppercase tracking-widest text-ang-faint flex items-center justify-between"
            style={{ borderTop: "1px solid var(--ang-border)" }}
          >
            <span>Demo build · no data entry · no approvals</span>
            <span className="tabular-nums">Angadya v0.1 · Jul 2026</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================================================================

const RoleCard = ({ role, index, bootstrap, selectedBranch, setSelectedBranch, selectedRegion, setSelectedRegion, onLogin }) => {
  const needs = role.requires;
  const accent = ROLE_ACCENT[role.id] || ROLE_ACCENT.owner;
  const { Icon } = accent;

  const scopeHint = useMemo(() => {
    if (role.id === "owner") return "27 branches · 102 SKUs · every module";
    if (role.id === "regional_manager") return "One Chennai cluster · 5-7 branches";
    if (role.id === "procurement_admin") return "Vendors · POs · inventory read-only";
    if (role.id === "hr_admin") return "27 branches · roster & coverage only";
    if (role.id === "store_manager") return "Single branch · all modules";
    return "";
  }, [role.id]);

  return (
    <div
      className="ang-panel ang-lift p-4 sm:p-5 flex items-start gap-4 ang-fade-up"
      data-testid={`role-card-${role.id}`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Accent icon tile */}
      <span
        className="shrink-0 inline-flex items-center justify-center rounded-2xl"
        style={{
          width: 44, height: 44,
          background: accent.grad,
          boxShadow: "0 6px 14px rgba(20,20,43,0.14)",
        }}
      >
        <Icon size={19} strokeWidth={2.2} style={{ color: "#FFFFFF" }} />
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base font-extrabold text-ang" style={{ letterSpacing: "-0.015em" }}>
            {role.label}
          </h3>
          {accent.tag && (
            <span
              className="rounded-full text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 text-white"
              style={{ background: accent.grad }}
            >
              {accent.tag}
            </span>
          )}
        </div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-ang-faint mt-0.5">
          {scopeHint}
        </div>
        <p className="text-sm text-ang-muted mt-2 leading-relaxed">{role.description}</p>

        {needs === "branch" && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <label className="text-[10px] font-bold uppercase tracking-widest text-ang-muted">Branch</label>
            <select
              data-testid={`select-branch-${role.id}`}
              value={selectedBranch || bootstrap.branches[0].id}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="text-sm font-semibold rounded-lg bg-white px-3 py-1.5 min-w-[200px] max-w-full outline-none focus:ring-2 focus:ring-[#00B4D8]/30"
              style={{ boxShadow: "inset 0 0 0 1px var(--ang-border-strong)" }}
            >
              {bootstrap.branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name} · {b.region}</option>
              ))}
            </select>
          </div>
        )}
        {needs === "region" && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <label className="text-[10px] font-bold uppercase tracking-widest text-ang-muted">Cluster</label>
            <select
              data-testid={`select-region-${role.id}`}
              value={selectedRegion || bootstrap.regions[0]}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="text-sm font-semibold rounded-lg bg-white px-3 py-1.5 min-w-[200px] max-w-full outline-none focus:ring-2 focus:ring-[#00B4D8]/30"
              style={{ boxShadow: "inset 0 0 0 1px var(--ang-border-strong)" }}
            >
              {bootstrap.regions.map((r) => {
                const count = bootstrap.branches.filter((b) => b.region === r).length;
                return (
                  <option key={r} value={r}>{r} Chennai — {count} branches</option>
                );
              })}
            </select>
          </div>
        )}
      </div>

      <button
        data-testid={`btn-enter-${role.id}`}
        onClick={() => onLogin(role)}
        className="self-center shrink-0 inline-flex items-center gap-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest px-4 py-2.5 text-white transition-transform hover:scale-[1.03]"
        style={{
          background: "linear-gradient(135deg, #00B4D8 0%, #7C6FF0 100%)",
          boxShadow: "0 6px 14px rgba(0,180,216,0.28)",
        }}
      >
        Enter <ArrowRight size={13} strokeWidth={2.4} />
      </button>
    </div>
  );
};

export default Landing;
